import { CardPrimitive } from '@figma/fpl-components';
import { borderRadius, color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { Card, CardActionArea, CardMedia } from '@/UI/Card/Card';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { Box } from '@/UI/Box/Box';
import { FlexCenHorVer } from '@/UI/styles';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { useIsHovered } from '@/hooks/useIsHovered';

const styles = stylex.create({
  root: {
    background: 'none',
    // Real CSS border (transparent at rest): an inset box-shadow would be occluded by the full-bleed
    // media <img>. box-sizing keeps the border from shifting the fixed 176px width.
    border: '1px solid transparent',
    borderRadius: borderRadius.medium,
    boxSizing: 'border-box',
    cursor: 'pointer',
    isolation: 'isolate',
    overflow: 'hidden',
    position: 'relative',
    width: '176px',
  },
  hovered: {
    borderColor: fplColor.border,
  },
  media: {
    display: 'block',
    height: '120px',
    objectFit: 'cover',
    position: 'relative',
    transition: 'transform 0.15s ease-out',
    width: '100%',
  },
  mediaScale: (transform: string) => ({ transform }),
  mainButton: {
    background: 'none',
    borderRadius: 'inherit',
    outline: {
      default: 'none',
      ':focus-visible': `2px solid ${fplColor.borderSelected}`,
    },
    outlineOffset: '-2px',
  },
});

type CardContent = {
  id: string;
  name?: string;
  poster?: string;
  icon?: React.ReactNode;
};

interface EducationGalleryCardProps {
  cardContent: CardContent;
  handleClick: (id: string) => void;
  showGradient?: boolean;
}

const renderOverlay = (isHovered: boolean, showGradient: boolean) => {
  if (showGradient) {
    return (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: isHovered ? 0.6 : 0.8,
          transition: 'opacity 0.15s ease-out',
          background: `linear-gradient(0deg, ${color.Black100} 0%, rgba(107, 107, 120, 0) 100%)`,
        }}
      />
    );
  }
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        opacity: isHovered ? 0.2 : 1,
        transition: 'opacity 0.15s ease-out',
        background: color.Black16_T,
      }}
    />
  );
};

function EducationGalleryCard({ cardContent, handleClick, showGradient }: EducationGalleryCardProps) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { isHovered, ...elementProps } = useIsHovered();

  if (isFplMigrationEnabled === true) {
    const actionLabel = cardContent.name
      ? t(I18N_KEYS.GENERAL.ARIA.OPEN_NAMED_ITEM, { name: cardContent.name })
      : t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.OPEN);
    return (
      <CardPrimitive.Root {...elementProps} {...stylex.props(styles.root, isHovered && styles.hovered)}>
        <CardPrimitive.MainButton
          {...stylex.props(styles.mainButton)}
          aria-label={actionLabel}
          onClick={() => handleClick(cardContent.id)}
        />
        <img
          {...stylex.props(styles.media, styles.mediaScale(isHovered ? 'scale(1.04)' : 'scale(1.02)'))}
          src={cardContent.poster || '/empty.png'}
          alt="content poster"
        />
        {renderOverlay(isHovered, showGradient || false)}
        {cardContent.icon && (
          <FlexCenHorVer
            sx={{
              position: 'absolute',
              inset: 0,
            }}
          >
            <Box
              sx={{
                transform: 'translateY(-5px)',
                color: isHovered ? color.White : fplColor.text,
                transition: 'color 0.15s ease-out',
              }}
            >
              {cardContent.icon}
            </Box>
          </FlexCenHorVer>
        )}
        {cardContent.name && (
          <EllipsisText
            color={color.White100}
            maxWidth="150px"
            variant="body-sm-rg"
            sx={{
              position: 'absolute',
              bottom: '8px',
              left: '12px',
            }}
          >
            {cardContent.name}
          </EllipsisText>
        )}
      </CardPrimitive.Root>
    );
  }

  return (
    <Card
      {...elementProps}
      key={cardContent.id}
      sx={{
        width: '176px',
        backgroundColor: 'transparent',
        backgroundImage: 'none',
        borderRadius: 2,
        border: '1px solid',
        transition: 'border-color 0.15s ease-out',
        borderColor: isHovered ? themeColor.white16_T : 'transparent',
        boxShadow: 'none',
      }}
      onClick={() => handleClick(cardContent.id)}
    >
      <CardActionArea>
        <CardMedia
          component="img"
          height={120}
          image={cardContent.poster || '/empty.png'}
          alt="content poster"
          sx={{
            transform: isHovered ? `scale(1.04)` : `scale(1.02)`,
            position: 'relative',
            transition: 'transform 0.15s ease-out',
          }}
        />
        {renderOverlay(isHovered, showGradient || false)}
        {cardContent.icon && (
          <FlexCenHorVer
            sx={{
              position: 'absolute',
              inset: 0,
            }}
          >
            <Box
              sx={{
                transform: 'translateY(-5px)',
                color: isHovered ? color.White : fplColor.text,
                transition: 'color 0.15s ease-out',
              }}
            >
              {cardContent.icon}
            </Box>
          </FlexCenHorVer>
        )}
        {cardContent.name && (
          <EllipsisText
            color={color.White100}
            maxWidth="150px"
            variant="body-sm-rg"
            sx={{
              position: 'absolute',
              bottom: '8px',
              left: '12px',
            }}
          >
            {cardContent.name}
          </EllipsisText>
        )}
      </CardActionArea>
    </Card>
  );
}

export default EducationGalleryCard;
